<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const searchQuery = ref('');
const groupName = ref('');
const selectedIds = ref([]);
const isSubmitting = ref(false);

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const isFetching = useMapGetter('agents/getUIFlags');

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  return (agents.value || [])
    .filter(agent => agent.id !== currentUser.value?.id && agent.confirmed)
    .filter(agent => {
      if (!query) return true;
      const name = (agent.name || '').toLowerCase();
      const email = (agent.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
});

const canSubmit = computed(
  () =>
    groupName.value.trim().length > 0 &&
    selectedIds.value.length > 0 &&
    !isSubmitting.value
);

onMounted(() => {
  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }
});

const resetForm = () => {
  searchQuery.value = '';
  groupName.value = '';
  selectedIds.value = [];
};

const open = () => {
  resetForm();
  if (!agents.value?.length) {
    store.dispatch('agents/get');
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const toggleAgent = agentId => {
  const id = Number(agentId);
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter(x => x !== id);
  } else {
    selectedIds.value = [...selectedIds.value, id];
  }
};

const isSelected = agentId => selectedIds.value.includes(Number(agentId));

const onConfirm = async () => {
  if (!canSubmit.value) return;
  isSubmitting.value = true;
  try {
    emit('create', {
      name: groupName.value.trim(),
      userIds: selectedIds.value,
    });
    close();
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="t('INTERNAL_CHAT.NEW_GROUP_TITLE')"
    :description="t('INTERNAL_CHAT.NEW_GROUP_DESCRIPTION')"
    :confirm-button-label="t('INTERNAL_CHAT.CREATE_GROUP')"
    :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-3">
      <div>
        <label class="block text-sm font-medium mb-1">
          {{ t('INTERNAL_CHAT.GROUP_NAME') }}
        </label>
        <input
          v-model="groupName"
          type="text"
          class="w-full rounded-lg border border-n-weak px-3 py-2 bg-n-background text-sm"
          :placeholder="t('INTERNAL_CHAT.GROUP_NAME_PLACEHOLDER')"
        />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">
          {{ t('INTERNAL_CHAT.GROUP_MEMBERS') }}
        </label>
        <input
          v-model="searchQuery"
          type="text"
          class="w-full rounded-lg border border-n-weak px-3 py-2 bg-n-background text-sm mb-2"
          :placeholder="t('INTERNAL_CHAT.SEARCH_AGENTS')"
        />
        <div
          v-if="isFetching.isFetching"
          class="text-sm text-n-slate-11 py-4 text-center"
        >
          {{ t('INTERNAL_CHAT.LOADING') }}
        </div>
        <div
          v-else-if="!filteredAgents.length"
          class="text-sm text-n-slate-11 py-4 text-center"
        >
          {{ t('INTERNAL_CHAT.NO_AGENTS') }}
        </div>
        <ul v-else class="flex flex-col gap-1 max-h-60 overflow-y-auto">
          <li v-for="agent in filteredAgents" :key="agent.id">
            <label
              class="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-2 cursor-pointer"
            >
              <input
                type="checkbox"
                class="rounded border-n-weak"
                :checked="isSelected(agent.id)"
                @change="toggleAgent(agent.id)"
              />
              <span
                class="flex items-center justify-center w-8 h-8 rounded-full bg-n-brand text-white text-xs font-medium shrink-0"
              >
                {{ (agent.name || agent.email || '?').charAt(0).toUpperCase() }}
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-medium truncate">{{
                  agent.name
                }}</span>
                <span class="block text-xs text-n-slate-11 truncate">{{
                  agent.email
                }}</span>
              </span>
            </label>
          </li>
        </ul>
        <p v-if="selectedIds.length" class="text-xs text-n-slate-11 mt-2">
          {{ t('INTERNAL_CHAT.SELECTED_COUNT', { count: selectedIds.length }) }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
